import type { OptionT } from '@dr.pogodin/react-utils';

import type { VoiceT } from './ai';
import type { PermissionSetT, RoleT } from './auth';

export const PAYMENT_METHODS = ['venmo', 'zelle'] as const;
export type PaymentMethodT = typeof PAYMENT_METHODS[number];

export const PAYMENT_METHOD_OPTIONS: Array<OptionT<string>> = [{
  name: 'Venmo',
  value: 'venmo' satisfies PaymentMethodT,
}, {
  name: 'Zelle',
  value: 'zelle' satisfies PaymentMethodT,
}];

/** Generic user profile data. Artists (practioners) have additional data
 *  associated to their profiles, which are stored in dedicated Arist objects. */
export type UserT = {
  address?: null | string;

  /**
   * Admin users (those with the "ADMIN" role) should have this field set
   * to the timestamp when they got the role assigned (the last time it was
   * assigned).
   */
  adminSince?: null | number;

  /** Avatar image. */
  avatarUrl?: null | string;

  city?: null | string;
  createdAt: number;

  /** The date of birth in YYYY-MM-DD format. Though, I am not yet sure if it is
   *  the best format to use here, thus no verification for it through the code. */
  dateOfBirth?: null | string;

  /** User email, if known. */
  email?: null | string;

  firstName: string;
  gender?: 'Female' | 'Male' | null;
  id: string;
  lastName: string;
  lastSeen: null | number | undefined;

  /**
   * BEWARE: This flag means the onboarding interview has been completed by
   * the user, and it has been posted to the backend for processing... it might
   * not be processed yet; this is intended for hiding the onboarding interview
   * panel as soon as the interview has been completed.
   */
  onboardingDone: boolean;

  passwordResetToken?: null | string;
  paymentMethod?: PaymentMethodT | null;
  permissions: PermissionSetT;
  phone?: null | string;
  phoneAllowsWhatsApp?: boolean | null;
  roles: RoleT[];

  /** Meaning the user country (as a part of his address). */
  state?: null | string;

  voice?: VoiceT | null;

  /**
   * This is currently set for applying practitioners if they have checked
   * the corresponding checkbox in the initial application form (at the main
   * website).
   */
  wantToScheduleIntroCall?: boolean | null;

  zipCode?: null | string;
};

// BEWARE: Some MongoDB aggregations set PublicUserDataT without being bound
// to this TypeScript definition explicitly; if you change this definition,
// make sure to check / update related aggregations.
// TODO: Can we connect the type of this definition with the types of related
// aggregations?
export type PublicUserDataT = Pick<UserT, 'avatarUrl' | 'firstName' | 'id' | 'lastName'>;

export function getUserDisplayName(user: PublicUserDataT | UserT): string {
  if (user.firstName || user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }

  if ('email' in user && user.email) return user.email;

  return user.id;
}

export type AdminStatusT = 'Active' | 'Inactive';

export function getAdminStatus(user: UserT): AdminStatusT {
  if (user.roles.includes('OWNER')) return 'Active';
  else if (user.adminSince) return 'Inactive';
  throw Error('Ther user is not an admin');
}

/**
 * User kinds for listing purposes.
 */
export const USER_LIST_KINDS = [
  'ADMIN',
  'ALL_USERS',
  'ARTIST',
  'CUSTOMER',
] as const;

/**
 * User list sortings.
 */
export const USER_LIST_SORTS = [
  'LAST_SEEN_ASC',
  'LAST_SEEN_DESC',
  'REGISTRATION_TIMESTAMP_ASC',
  'REGISTRATION_TIMESTAMP_DESC',
] as const;

export type UserListKindT = typeof USER_LIST_KINDS[number];
export type UserListSortT = typeof USER_LIST_SORTS[number];

export type UserListT = {
  /** Kind of the user list. */
  kind: UserListKindT;

  /** Total number of users matching the filter. */
  numTotal: number;

  /** Page index. */
  page: number;

  /** Full page size. */
  pageSize: number;

  /** Sorting of the list. */
  sort: UserListSortT;

  /** Users in the requested page. */
  users: UserT[];
};
